// src/services/salesService.js
//
// Frontend API wrapper for Sales Documents
// =============================================================================
//
// PURPOSE
// -------
// Provides one small, typed-ish API layer for the internal Reception Sales /
// Sales Documents module.
//
// WHY THIS EXISTS
// ---------------
// React pages should not know backend URL details or Axios error shapes. This
// service follows the same hb_visits pattern as productsService.js,
// servicesCatalogService.js and stockService.js:
// - use the central apiClient,
// - keep credentials/session headers centralized,
// - normalize errors into regular Error objects,
// - leave final business validation to the backend.
//
// IMPORTANT BUSINESS MODEL
// ------------------------
// A sales document can contain product lines and service lines.
// - Product lines create automatic SALE stock movements when the document is
//   posted.
// - Service lines do not reduce stock in Phase 2.
//
// The frontend may show estimated totals, but backend totals returned from this
// service are the source of truth.

import apiClient from "../utils/apiClient";
import { idempotentPost } from "../utils/idempotentStockPosting";

const DEBUG_API =
  String(process.env.REACT_APP_DEBUG_API || "").toLowerCase().trim() === "true";

export const SALES_DOCUMENT_TYPES = Object.freeze({
  INVOICE: "invoice",
  RECEIPT: "receipt",
  INTERNAL_SALE: "internal_sale",
});

export const SALES_DOCUMENT_STATUSES = Object.freeze({
  DRAFT: "draft",
  POSTED: "posted",
  CANCELLED: "cancelled",
});

export const SALES_LINE_TYPES = Object.freeze({
  PRODUCT: "product",
  SERVICE: "service",
  MANUAL: "manual",
});

function getErrorMessage(err) {
  const apiMsg =
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.response?.data?.detail;

  return apiMsg || err?.message || "Request failed";
}

/**
 * Build an operator-friendly PDF download error.
 *
 * The invoice PDF endpoint is requested with responseType="blob". When the
 * backend returns JSON for a 4xx/5xx error, Axios may expose the payload as a
 * Blob instead of a plain object. For the Sales Overview UI this low-level shape
 * is not useful; operators should not see messages such as
 * "downloadSalesDocumentPdf: Request failed with status code 500".
 *
 * The returned error keeps status/code/debug data for developers but exposes a
 * stable business code that the page can translate.
 */
function buildPdfDownloadError(err) {
  const e = new Error("Invoice PDF could not be generated.");

  e.status = err?.response?.status || err?.status;
  e.outcomeUnknown = Boolean(err?.outcomeUnknown || err?.response?.data?.outcome_unknown);
  e.source_context = err?.response?.data?.source_context || null;
  e.diagnostic_id = err?.response?.data?.diagnostic_id || null;
  e.data = err?.response?.data || err?.data;
  e.code = err?.response?.data?.code || err?.code || "INVOICE_PDF_DOWNLOAD_FAILED";
  e.userMessageKey = "sales:messages.pdfOpenFailed";

  if (DEBUG_API) {
    // eslint-disable-next-line no-console
    console.error("[SalesService] downloadSalesDocumentPdf", {
      status: e.status,
      code: e.code,
      originalMessage: err?.message,
      data: e.data,
    });
  }

  return e;
}

/**
 * Normalize Axios errors for page components.
 *
 * The pages can simply display error.message, but the original backend status,
 * code and response body remain attached for debugging and future field-level
 * validation handling.
 */
function throwApiError(context, err) {
  const message = getErrorMessage(err);
  const e = new Error(`${context}: ${message}`);

  e.status = err?.response?.status || err?.status;
  e.outcomeUnknown = Boolean(err?.outcomeUnknown || err?.response?.data?.outcome_unknown);
  e.source_context = err?.response?.data?.source_context || null;
  e.diagnostic_id = err?.response?.data?.diagnostic_id || null;
  e.data = err?.response?.data || err?.data;
  e.code = err?.response?.data?.code || err?.code;

  if (DEBUG_API) {
    // eslint-disable-next-line no-console
    console.error(`[SalesService] ${context}`, {
      status: e.status,
      code: e.code,
      message,
      data: e.data,
    });
  }

  throw e;
}

function isBlank(value) {
  return value === undefined || value === null || `${value}`.trim() === "";
}

function assertId(context, id) {
  if (isBlank(id)) {
    const e = new Error(`${context}: id is required.`);
    e.code = "SALES_DOCUMENT_ID_MISSING";
    throw e;
  }
}

function assertPayload(context, payload) {
  if (!payload || typeof payload !== "object") {
    const e = new Error(`${context}: payload object is required.`);
    e.code = "SALES_DOCUMENT_PAYLOAD_MISSING";
    throw e;
  }
}

export const fetchSalesDocuments = async (params = {}) => {
  try {
    const res = await apiClient.get("/sales/documents", {
      withCredentials: true,
      params,
    });

    return Array.isArray(res?.data) ? res.data : [];
  } catch (err) {
    throwApiError("fetchSalesDocuments", err);
  }
};

export const fetchSalesDocumentById = async (id) => {
  try {
    assertId("fetchSalesDocumentById", id);

    const res = await apiClient.get(`/sales/documents/${id}`, {
      withCredentials: true,
    });

    return res?.data ?? null;
  } catch (err) {
    throwApiError("fetchSalesDocumentById", err);
  }
};

export const createSalesDocument = async (payload) => {
  try {
    assertPayload("createSalesDocument", payload);

    const res = await apiClient.post("/sales/documents", payload, {
      withCredentials: true,
      headers: { "Content-Type": "application/json" },
    });

    return res?.data;
  } catch (err) {
    throwApiError("createSalesDocument", err);
  }
};

export const updateSalesDocument = async (id, payload) => {
  try {
    assertId("updateSalesDocument", id);
    assertPayload("updateSalesDocument", payload);

    const res = await apiClient.put(`/sales/documents/${id}`, payload, {
      withCredentials: true,
      headers: { "Content-Type": "application/json" },
    });

    return res?.data;
  } catch (err) {
    throwApiError("updateSalesDocument", err);
  }
};

/**
 * Post a sales document.
 *
 * Important: this is not a simple status update. The backend will allocate the
 * final document number and create SALE stock movements for product lines inside
 * one transaction. The frontend therefore treats this call as the operational
 * commit action.
 */
export const postSalesDocument = async (id) => {
  try {
    assertId("postSalesDocument", id);

    const res = await idempotentPost(apiClient, `/sales/documents/${id}/post`, {}, {
      withCredentials: true,
      headers: { "Content-Type": "application/json" },
    });

    return res?.data;
  } catch (err) {
    throwApiError("postSalesDocument", err);
  }
};

export const cancelSalesDocument = async (id) => {
  try {
    assertId("cancelSalesDocument", id);

    const res = await apiClient.post(`/sales/documents/${id}/cancel`, {}, {
      withCredentials: true,
      headers: { "Content-Type": "application/json" },
    });

    return res?.data;
  } catch (err) {
    throwApiError("cancelSalesDocument", err);
  }
};

/**
 * Download the snapshot-based invoice PDF for a posted sales document.
 *
 * The backend endpoint renders the already-posted document from immutable
 * snapshots. The frontend receives a Blob so it can open/download the document
 * without bypassing apiClient authentication/session headers.
 */
export const downloadSalesDocumentPdf = async (id) => {
  try {
    assertId("downloadSalesDocumentPdf", id);

    const res = await apiClient.get(`/sales/documents/${id}/pdf`, {
      withCredentials: true,
      responseType: "blob",
    });

    const contentDisposition = res?.headers?.["content-disposition"] || "";
    const filenameMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
    const filename = filenameMatch
      ? decodeURIComponent(filenameMatch[1] || filenameMatch[2] || "invoice.pdf")
      : `sales-document-${id}.pdf`;

    return {
      blob: res?.data,
      filename,
    };
  } catch (err) {
    throw buildPdfDownloadError(err);
  }
};

/**
 * Search products with sales context.
 *
 * The backend endpoint enriches product catalog data with stock availability and
 * current cost information. The UI uses this for selection and warnings only;
 * the backend recalculates and validates everything again during posting.
 */
export const searchSalesProducts = async (params = {}) => {
  try {
    const res = await apiClient.get("/sales/products/search", {
      withCredentials: true,
      params,
    });

    return Array.isArray(res?.data) ? res.data : [];
  } catch (err) {
    throwApiError("searchSalesProducts", err);
  }
};

export const searchSalesServices = async (params = {}) => {
  try {
    const res = await apiClient.get("/sales/services/search", {
      withCredentials: true,
      params,
    });

    return Array.isArray(res?.data) ? res.data : [];
  } catch (err) {
    throwApiError("searchSalesServices", err);
  }
};

const salesService = {
  fetchSalesDocuments,
  fetchSalesDocumentById,
  createSalesDocument,
  updateSalesDocument,
  postSalesDocument,
  cancelSalesDocument,
  downloadSalesDocumentPdf,
  searchSalesProducts,
  searchSalesServices,
};

export default salesService;




